<script setup lang="ts">
import { computed, ref } from "vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";

const props = defineProps<{
  services: any[];
  loading?: boolean;
}>();

defineEmits<{
  (event: "open-drawer", service: any): void;
}>();

const expanded = ref(false);

const displayedServices = computed(() => {
  if (!expanded.value) {
    return props.services.slice(0, 6);
  }
  return props.services;
});
</script>

<template>
  <div class="text-left space-y-6">
    <!-- Loading Skeletons -->
    <div
      v-if="loading"
      class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6"
    >
      <Skeleton
        v-for="n in 6"
        :key="n"
        class="h-56 rounded-3xl bg-slate-100 animate-pulse"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="services.length === 0"
      class="border border-dashed border-slate-200 bg-white p-12 rounded-3xl text-center space-y-3"
    >
      <div
        class="h-12 w-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mx-auto text-slate-400"
      >
        <DMSuiteIcon
          :icon="DASHBOARD_ICONS.layers"
          :size="24"
          custom-class="text-slate-400"
        />
      </div>
      <div class="space-y-1">
        <p class="text-base font-bold text-slate-800">
          No services match your criteria
        </p>
        <p class="text-xs text-slate-400 font-medium">
          Try clearing search filters to see all services in your plan.
        </p>
      </div>
    </div>

    <!-- Cards Grid -->
    <div
      v-else
      class="space-y-6"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
        <div
          v-for="service in displayedServices"
          :key="service.id"
          class="bg-white border border-slate-200/80 rounded-3xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between text-left p-6 min-h-[16rem] min-w-0"
        >
          <div class="space-y-4 min-w-0">
            <!-- Header Row: Icon, Service Name & Status Badge -->
            <div class="flex items-start justify-between gap-3 min-w-0">
              <div class="flex items-center gap-3 min-w-0 flex-1">
                <div
                  class="flex items-center justify-center shrink-0 h-12 w-12 rounded-2xl border shadow-2xs"
                  :class="service.iconWrapClass"
                >
                  <DMSuiteIcon
                    v-if="typeof service.icon === 'string'"
                    :icon="service.icon"
                    :size="24"
                    :custom-class="service.iconClass"
                  />
                  <component
                    :is="service.icon"
                    v-else
                    class="size-6"
                    :class="service.iconClass"
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <h4 class="font-black text-slate-900 truncate leading-snug text-base md:text-lg">
                    {{ service.label }}
                  </h4>
                  <p class="text-xs text-slate-500 font-semibold truncate mt-0.5">
                    {{ service.description }}
                  </p>
                </div>
              </div>

              <!-- Status Badge -->
              <Badge
                variant="outline"
                class="rounded-full font-bold px-2.5 py-0.5 text-[11px] whitespace-nowrap shrink-0 border"
                :class="service.badgeClass"
              >
                {{ service.status }}
              </Badge>
            </div>

            <!-- Deliverable / Milestone Summary -->
            <div class="rounded-2xl bg-slate-50/70 border border-slate-100 p-3 space-y-1">
              <div class="flex items-center justify-between text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                <span>Deliverable Status</span>
                <span v-if="service.totalTasks > 0" class="text-slate-500 lowercase font-medium">
                  {{ service.completedTasks }} of {{ service.totalTasks }} done
                </span>
              </div>
              <p class="text-xs font-bold text-slate-700 truncate">
                {{ service.nextDeliverable }}
              </p>
            </div>

            <!-- Progress Bar / Workload Gauge -->
            <div class="space-y-1.5">
              <div class="flex items-center justify-between font-bold text-xs">
                <span class="text-slate-400 uppercase tracking-wider">Progress</span>
                <span
                  v-if="service.totalTasks > 0"
                  :class="service.valueClass"
                >
                  {{ service.progress }}%
                </span>
                <span
                  v-else
                  class="text-xs font-semibold text-slate-400 lowercase"
                >
                  on-demand
                </span>
              </div>

              <div class="w-full bg-slate-100 rounded-full overflow-hidden h-2.5">
                <div
                  v-if="service.totalTasks > 0"
                  class="h-full rounded-full transition-all duration-500"
                  :class="service.progressClass"
                  :style="{ width: `${service.progress}%` }"
                />
                <div
                  v-else
                  class="h-full bg-slate-200/60 rounded-full w-full"
                />
              </div>
            </div>
          </div>

          <!-- Footer Row: Tasks count and View Details button -->
          <div class="flex items-center justify-between border-t border-slate-100 pt-4 mt-4">
            <div class="text-xs text-slate-400 font-semibold">
              <span v-if="service.totalTasks > 0" class="text-slate-600 font-bold">
                {{ service.completedTasks }}/{{ service.totalTasks }} Tasks
              </span>
              <span v-else class="text-slate-400">
                On-Demand Service
              </span>
            </div>

            <Button
              size="sm"
              variant="outline"
              class="font-bold text-[#1f7aff] border-blue-200 bg-white hover:bg-blue-50/60 hover:text-[#185adb] h-9 px-4 text-xs rounded-xl shadow-xs transition-all"
              @click="$emit('open-drawer', service)"
            >
              View Details
            </Button>
          </div>
        </div>
      </div>

      <!-- Expansion Trigger (Toggle to view all or collapse) -->
      <div
        v-if="services.length > 6"
        class="flex justify-center pt-2"
      >
        <Button
          variant="outline"
          class="w-full sm:w-auto min-h-11 rounded-2xl border-blue-200 bg-[#f6f9ff] px-8 text-sm font-bold text-[#1f7aff] hover:bg-[#eef4ff] hover:text-[#185adb] shadow-xs flex items-center justify-center gap-2 transition-all"
          @click="expanded = !expanded"
        >
          <span>{{ expanded ? 'Show Fewer Services' : `View All Services (${services.length})` }}</span>
          <DMSuiteIcon
            v-if="expanded"
            :icon="DASHBOARD_ICONS.chevronUp"
            :size="15"
          />
          <DMSuiteIcon
            v-else
            :icon="DASHBOARD_ICONS.chevronDown"
            :size="15"
          />
        </Button>
      </div>
    </div>
  </div>
</template>
